"use client";

import React, { useState, useEffect, useRef } from "react";
import Button from "src/app/components/ui/Button";
import Select from "../../../ui/Form/Select/select2";
import { statesData } from "../../../constants/statesData";
import { useResponsiveWidth } from "../../../hooks/useWindowWidth";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
// import { useRouter } from "next/navigation";
import {
  SearchIcon,
  InputBtn,
  PropertySearchData,
  propertyTypes,
  getPriceRanges,
  propertyData,
  parsePrice,
} from "./HeroSearchComponent";
import { CompanyLogo } from "../NavBar";
import { useMemo } from "react";
import { usePathname } from "next/navigation";
import {
  getMatchingAreas,
  StatesData,
} from "src/app/components/constants/helperClient";

import useUnitsData from "src/app/components/hooks/useUnitInfo";
import { useRouteLoadingReset } from "src/app/components/hooks/useRouterLoading";

export function getMatchingKeys(
  a: string[],
  b: Record<string, string>
): string[] {
  // Return early if either the keys array or the object is incomplete
  if (!a || a.length === 0 || !b || Object.keys(b).length === 0) {
    return [];
  }

  // Normalize the keys in object B to lowercase for comparison
  const normalizedObjectKeys = Object.keys(b).reduce((acc, key) => {
    acc[key.toLowerCase()] = key; // Store original keys with lowercase keys as the reference
    return acc;
  }, {} as Record<string, string>);

  const result: string[] = [];

  // Compare the keys in array A to the normalized object keys
  a.forEach((key) => {
    const lowerKey = key.toLowerCase(); // Normalize current key in array A
    if (normalizedObjectKeys[lowerKey]) {
      result.push(normalizedObjectKeys[lowerKey]); // Add matched key to result array
    }
  });

  return result;
}
export function usePropertyPurpose(): string | null {
  const pathname = usePathname()?.toLowerCase();

  return useMemo(() => {
    if (!pathname) return null;

    const map: Record<string, string> = {
      rent: "For rent",
      shortlet: "For shortlet",
      sale: "For sale",
    };

    for (const keyword in map) {
      if (pathname.includes(keyword)) return map[keyword];
    }

    return null;
  }, [pathname]);
}
export const removeUrlParam = (field: keyof PropertySearchData) => {
  const currentUrl = new URL(window.location.href);
  const searchParams = new URLSearchParams(currentUrl.search);
  // Check if the field exists in the URL and remove it
  if (searchParams.has(field)) {
    searchParams.delete(field);
  }
  // Update the URL after removing the parameter
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}?${searchParams.toString()}`
  );
};
export const removeMany = (fields: (keyof PropertySearchData)[]) => {
  fields.forEach((field) => removeUrlParam(field));
};

export default function PropertySearchForm({
  navigate,
}: {
  navigate?: boolean;
}) {
  const pathname = usePathname()?.toLowerCase();
  const url = usePropertyPurpose();
  const searchParams = useSearchParams();
  const GetFromUrl = (name: string): string | null => {
    return searchParams.get(name);
  };
  const [isActive, setIsActive] = useState("");
  const [formData, setFormData] = useState<PropertySearchData>({
    search: GetFromUrl("search") || "",
    propertyType: (usePropertyPurpose() as "For rent") || "",
    category: GetFromUrl("category") || "",
    unit_type: GetFromUrl("unit_type") || "",
    min_price: GetFromUrl("min_price") || "",
    max_price: GetFromUrl("max_price") || "",
    unit_sub_type: GetFromUrl("unit_sub_type") || "",
    priceRange: "",
    state: GetFromUrl("state") || "",
    local_government: GetFromUrl("local_government") || "",
  });

  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  const { minPrices, maxPrices } = getPriceRanges(formData.propertyType);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isAtTop, setIsAtTop] = useState(false);
  const targetDivRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (targetDivRef.current) {
        const { top } = targetDivRef.current.getBoundingClientRect();
        setIsAtTop(top <= 0); // True when the div reaches the top of the viewport
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleChange = (field: keyof PropertySearchData) => (value: string) => {
    setFormData((prevData) => {
      let updatedData = { ...prevData, [field]: value };
      // Handle specific fields where resetting other fields is required
      if (field === "propertyType" && prevData.propertyType !== value) {
        updatedData = {
          ...updatedData,
          category: "",
          unit_type: "",
          unit_sub_type: "",
        };
        removeMany(["category", "unit_type", "unit_sub_type"]);
      }
      if (field === "category" && prevData.category !== value) {
        updatedData = { ...updatedData, unit_type: "", unit_sub_type: "" };
        removeMany(["unit_sub_type", "unit_type"]);
      }
      if (field === "unit_type" && prevData.unit_type !== value) {
        updatedData = { ...updatedData, unit_sub_type: "" };
        removeUrlParam("unit_sub_type");
      }
      if (field === "state" && prevData.state !== value) {
        updatedData = {
          ...updatedData,
          local_government: "", // Reset localGovernment (lg)
        };
        removeUrlParam("local_government");
      }
      if (value === "") {
        removeUrlParam(field);
      }
      return updatedData;
    });
  };

  const handleSearch = () => {
    if (pathname === "/") {
      setLoading(true);
    }
    const { category, min_price, max_price, propertyType, ...filters } =
      formData;
    const searchParams = new URLSearchParams(window.location.search);
    // Helper function to clean and convert property type to lowercase
    const cleanPropertyType = (type: string) => {
      let cleaned = type
        .replace(/\bfor\b/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();

      if (cleaned === "short-let") cleaned = "shortlet"; // Special case for "short-let"
      return cleaned;
    };
    // Convert price to a clean numeric value
    const convertPrice = (price: string) => {
      if (!price || price === "No Min" || price === "No Max") return "";
      return price
        .replace("₦", "")
        .replace(" Million", "000000")
        .replace(" Billion", "000000000")
        .replace(/,/g, "");
    };
    // Set price parameters (if present), making sure both keys and values are lowercase
    if (min_price)
      searchParams.set("min_price", convertPrice(min_price).toLowerCase());
    if (max_price)
      searchParams.set("max_price", convertPrice(max_price).toLowerCase());
    // Set category parameter (if present) and ensure both key and value are lowercase
    if (category) searchParams.set("category", category.toLowerCase());
    // Loop through filters, ensuring both keys and values are lowercase
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key.toLowerCase(), value.toLowerCase()); // Ensure key and value are lowercase
      }
    });
    let newPath = "/properties";
    if (!navigate) {
      newPath = window.location.pathname; // Stay on the current path
    } else if (propertyType) {
      newPath += `/${cleanPropertyType(propertyType)}`;
    }
    // Create the final URL
    const finalUrl = `${newPath}?${searchParams.toString()}`;
    // Perform navigation or update URL without reload
    if (navigate) {
      router.push(finalUrl); // Full navigation
    } else {
      router.replace(finalUrl, { scroll: false }); // Update URL without reload or navigation
      // window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };
  useRouteLoadingReset(loading, setLoading);
  const res = useUnitsData(); // Hook to fetch unit data
  const states = res.unitsData?.cleanedAreas;
  const areas = res.unitsData?.unitInfo;

  const statesToMap = isDemo ? statesData : states;
  const categories = Object.keys(propertyData);
  const typesTest = formData.category
    ? Object.keys(propertyData[formData.category])
    : [];
  const item = getMatchingKeys(
    typesTest,
    areas as unknown as Record<string, string>
  );
  const types = isDemo ? typesTest : item || [];
  const subtypes = isDemo
    ? formData.category && formData.unit_type
      ? propertyData[formData.category][formData.unit_type]
      : []
    : areas && formData.unit_type
    ? areas[formData.unit_type]
    : [];
  const stateNames = Array.isArray(statesToMap)
    ? statesToMap
        .map((state) => {
          const keys = Object.keys(state);
          return keys.length > 0 ? keys[0] : null;
        })
        .filter((key): key is string => key !== null)
    : [];
  const selectedStateData = Array.isArray(statesToMap)
    ? statesToMap.find((state) =>
        Object.prototype.hasOwnProperty.call(state, formData.state)
      )
    : undefined;
  const localGovernments = selectedStateData
    ? Object.keys(
        selectedStateData[
          formData.state as keyof typeof selectedStateData
        ] as object
      )
    : [];
  const [openAll, setOpenAll] = useState(false);

  const { isMobile } = useResponsiveWidth();

  return (
    <div
      ref={targetDivRef}
      className={`property-search-form z-[90]  flex top-0 py-4 ${
        !isMobile && "sticky"
      } bg-white z-[50] shadow-sm`}
    >
      <div className="parent   wrapper items-center  gap-10 flex">
        <div className={`xl:hidden  ${isAtTop ? "visible" : "invisible"}`}>
          <Link href="/">
            <CompanyLogo />
          </Link>
        </div>
        <form className={`flex flex-col xl:w-max xl:mx-auto xl:items-center`}>
          <div className="flex xl:mx-auto xl:self-center xs:!w-auto xl:w-max justify-between xl:justify-center  items-center gap-4">
            <div
            role="button"
            aria-expanded={openAll}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsActive("form");
              }}
              className={`item border h-full xs:w-[50%]  min-h-[45px] p-2 py-0 border-label-main rounded-md items-center flex gap-2 ${
                isActive === "form" && "!border-themeColor"
              }`}
            >
              <SearchIcon />
              <input
                id="keyword"
                name="keyword"
                placeholder="Search with ID or title"
                value={GetFromUrl("category") || formData.search}
                onChange={(e) => handleChange("search")(e.target.value)}
                className="focus:outline-none outline-none xl:w-[70%]  w-[400px] text-disabled-main border-none rounded"
              />

              <div
                style={{
                  backgroundColor: "rgba(var(--primary-color-rgb), 0.6)",
                }}
                onClick={() => {
                  setOpenAll((prev) => !prev);
                }}
                className={`search place-items-center h-full  size-8 rounded hidden lg:grid`}
              >
                <SearchBtn
                  color={`${
                    openAll ? "var(--primary-color)" : "var(--primary-color)"
                  }`}
                />
              </div>
            </div>
            <div className="items lg:hidden flex gap-4">
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isActive === "lg") {
                    setIsActive("");
                    return;
                  }
                  setIsActive("lg");
                }}
                className=""
              >
                <InputBtn isActive={isActive === "lg"} placeholder="Location" />
              </div>
              <div>
                <Select
                  onClick={() => {
                    if (isActive === "type") {
                      setIsActive("");
                      return;
                    }
                    setIsActive("type");
                  }}
                  borderStyle={`${isActive === "type" && "!border-themeColor"}`}
                  placeholder="Property Type"
                  clickable={true}
                  name="propertyType"
                  inputW={95}
                  readonly={url ? true : false}
                  value={formData.propertyType}
                  options={propertyTypes}
                  onChange={handleChange("propertyType")}
                />
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (isActive === "range") {
                    setIsActive("");
                    return;
                  }
                  setIsActive("range");
                }}
                className=""
              >
                <InputBtn
                  isActive={isActive === "range"}
                  placeholder="Price Range"
                />
              </div>
            </div>

            {isActive !== "type" &&
              isActive !== "lg" &&
              isActive !== "range" && (
                <Button
                  onClick={handleSearch}
                  className="bg-themeColor primary-btn h-full text-white py-[11px] px-9"
                >
                  {loading ? "Loading" : "Search"}
                </Button>
              )}
          </div>
          <div
            className={`all  mt-4    xl:flex gap-3 lg:flex-col ${
              openAll ? "block" : "lg:!hidden"
            } `}
          >
            <div className="items justify-center   hidden xl:flex gap-4">
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isActive === "lg") {
                    setIsActive("");
                    return;
                  }
                  setIsActive("lg");
                }}
                className=""
              >
                <InputBtn isActive={isActive === "lg"} placeholder="Location" />
              </div>
              <div>
                <Select
                  onClick={() => {
                    if (isActive === "type") {
                      setIsActive("");
                      return;
                    }
                    setIsActive("type");
                  }}
                  borderStyle={`${isActive === "type" && "!border-themeColor"}`}
                  placeholder="Property Type"
                  clickable={true}
                  name="propertyType"
                  readonly={url ? true : false}
                  inputW={95}
                  value={formData.propertyType}
                  options={propertyTypes}
                  onChange={handleChange("propertyType")}
                />
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (isActive === "range") {
                    setIsActive("");
                    return;
                  }
                  setIsActive("range");
                }}
                className=""
              >
                <InputBtn
                  isActive={isActive === "range"}
                  placeholder="Price Range"
                />
              </div>
            </div>

            {isActive === "type" && formData.propertyType !== "" && (
              <div className="flex mt-5 xl:justify-center justify-between xs:gap-2 gap-4">
                <Select
                  placeholder="Select Category"
                  className="w-full"
                  name="category"
                  value={formData.category}
                  options={categories}
                  onChange={handleChange("category")}
                />
                <Select
                  placeholder="Select Type"
                  className="w-full"
                  name="type"
                  value={formData.unit_type}
                  options={types}
                  onChange={handleChange("unit_type")}
                />
                <Select
                  placeholder="Select Subtype"
                  className="w-full"
                  name="subtype"
                  value={formData.unit_sub_type}
                  options={subtypes}
                  onChange={handleChange("unit_sub_type")}
                />
                <Button
                  onClick={handleSearch}
                  className="bg-themeColor primary-btn h-full text-white py-2 px-9"
                >
                  Search
                </Button>
              </div>
            )}
            {isActive === "range" && (
              <div className="flex mt-5 xl:justify-center xs:gap-2 justify-between gap-4">
                <Select
                  placeholder="Minimum Price"
                  inputW={400}
                  name="minPrice"
                  value={formData.min_price}
                  options={minPrices}
                  onChange={handleChange("min_price")}
                />
                <Select
                  placeholder="Maximum Price"
                  inputW={250}
                  name="maxPrice"
                  value={formData.max_price}
                  options={
                    formData.min_price
                      ? maxPrices.filter((max) => {
                          const minVal = parsePrice(formData.min_price);
                          const maxVal = parsePrice(max);

                          // Allow max if it's not a number (e.g., "No Max")
                          if (maxVal === null) return true;
                          if (minVal === null) return true;

                          return maxVal >= minVal;
                        })
                      : maxPrices
                  }
                  onChange={handleChange("max_price")}
                />

                <Button
                  onClick={handleSearch}
                  className="bg-themeColor primary-btn h-full text-white py-2 px-9"
                >
                  Search
                </Button>
              </div>
            )}
            {isActive === "lg" && (
              <div className="flex mt-5 xl:justify-center xs:gap-2 gap-4">
                <Select
                  inputW={310}
                  placeholder="Select State"
                  name="state"
                  value={formData.state}
                  options={stateNames}
                  onChange={(value: string) => {
                    handleChange("state")(value);
                    handleChange("local_government")("");
                  }}
                />
                <Select
                  inputW={310}
                  placeholder="Local Government"
                  value={formData.local_government}
                  name="lga"
                  options={localGovernments}
                  onChange={(value: string) => {
                    handleChange("local_government")(value);
                  }}
                />
                <Button
                  onClick={handleSearch}
                  className="bg-themeColor primary-btn h-full text-white py-2 px-9"
                >
                  Search
                </Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const SearchBtn = ({ color }: { color: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.25 10H6.895M2.534 10H0.75M2.534 10C2.534 9.42184 2.76368 8.86735 3.17251 8.45852C3.58134 8.04969 4.13583 7.82001 4.714 7.82001C5.29217 7.82001 5.84666 8.04969 6.25549 8.45852C6.66432 8.86735 6.894 9.42184 6.894 10C6.894 10.5782 6.66432 11.1327 6.25549 11.5415C5.84666 11.9503 5.29217 12.18 4.714 12.18C4.13583 12.18 3.58134 11.9503 3.17251 11.5415C2.76368 11.1327 2.534 10.5782 2.534 10ZM19.25 16.607H13.502M13.502 16.607C13.502 17.1853 13.2718 17.7404 12.8628 18.1494C12.4539 18.5583 11.8993 18.788 11.321 18.788C10.7428 18.788 10.1883 18.5573 9.77951 18.1485C9.37068 17.7397 9.141 17.1852 9.141 16.607M13.502 16.607C13.502 16.0287 13.2718 15.4746 12.8628 15.0657C12.4539 14.6567 11.8993 14.427 11.321 14.427C10.7428 14.427 10.1883 14.6567 9.77951 15.0655C9.37068 15.4743 9.141 16.0288 9.141 16.607M9.141 16.607H0.75M19.25 3.39301H16.145M11.784 3.39301H0.75M11.784 3.39301C11.784 2.81484 12.0137 2.26035 12.4225 1.85152C12.8313 1.44269 13.3858 1.21301 13.964 1.21301C14.2503 1.21301 14.5338 1.2694 14.7983 1.37896C15.0627 1.48851 15.3031 1.64909 15.5055 1.85152C15.7079 2.05395 15.8685 2.29427 15.9781 2.55876C16.0876 2.82325 16.144 3.10673 16.144 3.39301C16.144 3.67929 16.0876 3.96277 15.9781 4.22726C15.8685 4.49175 15.7079 4.73207 15.5055 4.93451C15.3031 5.13694 15.0627 5.29751 14.7983 5.40707C14.5338 5.51663 14.2503 5.57301 13.964 5.57301C13.3858 5.57301 12.8313 5.34333 12.4225 4.93451C12.0137 4.52568 11.784 3.97118 11.784 3.39301Z"
        stroke={`${color}`}
        stroke-width="1.5"
        stroke-miterlimit="10"
        stroke-linecap="round"
      />
    </svg>
  );
};

const unit_state = ["Oyo", "Abia", "Akwa Ibom"];
const unit_area = ["Apete/Ayegun/Awotan", "Amawom", "Akpabom"];
const unit_lga = ["Ido", "Ikwuano", "Eastern Obolo"];

// Convert statesData to object
export const statesDataObject: StatesData = statesData.reduce(
  (acc, stateObj) => {
    const stateName = Object.keys(stateObj)[0];
    acc[stateName] = stateObj[stateName as keyof typeof stateObj] as {
      [lga: string]: string[];
    };
    return acc;
  },
  {} as StatesData
);

export const matchedData = getMatchingAreas(
  unit_state,
  unit_area,
  unit_lga,
  statesDataObject
);



type StatesDataType = { [state: string]: { [lga: string]: string[] } };

export function transformToArray(
  statesData: StatesDataType
): { [state: string]: { [lga: string]: string[] } }[] {
  const result: { [state: string]: { [lga: string]: string[] } }[] = [];

  // Iterate over each state in the statesData object
  Object.keys(statesData).forEach((state) => {
    const stateData = { [state]: statesData[state] };
    result.push(stateData);
  });

  return result;
}
